import { Button } from '@mantine/core';
import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './LedgerRow.module.css';

export interface LedgerChanged {
  /** The default value in words, shown as "Default: X". */
  defaultLabel: string;
  onReset: () => void;
}

export interface LedgerEditor {
  /** Replaces the value area until `close()` is called. */
  render: (close: () => void) => ReactNode;
  /** Text of the Edit control. Defaults to "Edit". */
  label?: string;
}

export interface LedgerRowProps {
  label: ReactNode;
  /** Never collapses. A string is shown bold, like the design's value line. */
  value: ReactNode;
  help?: ReactNode;
  changed?: LedgerChanged;
  action?: ReactNode;
  editor?: LedgerEditor;
  /** A red line under the value, for conditions that reject, drop or fail to deliver. */
  risk?: ReactNode;
  id?: string;
  /** Room for a long label such as an email address. */
  wideLabel?: boolean;
}

export function ChangedMarker({ defaultLabel, onReset }: LedgerChanged) {
  const { t } = useTranslation();
  return (
    <span className={classes.changed}>
      <b className={classes.changedMark}>{t('ui.changed')}</b>
      <span>{t('ui.defaultValue', { value: defaultLabel })}</span>
      <span aria-hidden="true">·</span>
      <button type="button" className={classes.reset} onClick={onReset}>
        {t('ui.reset')}
      </button>
    </span>
  );
}

export function LedgerRow({
  label,
  value,
  help,
  changed,
  action,
  editor,
  risk,
  id,
  wideLabel,
}: LedgerRowProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const editButton = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);

  useEffect(() => {
    if (!editing && returnFocus.current) {
      returnFocus.current = false;
      editButton.current?.focus();
    }
  }, [editing]);

  const close = () => {
    returnFocus.current = true;
    setEditing(false);
  };

  // Escape closes any editor; text editors handle it themselves, an open dropdown closes first.
  const onEditorKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    if ((e.target as HTMLElement).getAttribute('aria-expanded') === 'true') return;
    e.preventDefault();
    close();
  };

  const editLabel = editor?.label ?? t('ui.edit');
  const editAriaLabel =
    !editor?.label && typeof label === 'string' ? t('ui.editLabel', { label }) : undefined;

  return (
    <div className={wideLabel ? `${classes.row} ${classes.wide}` : classes.row} id={id}>
      <div className={classes.label}>{label}</div>
      {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/noNoninteractiveElementInteractions: catches Escape bubbling up from the editor's own controls */}
      <div className={classes.value} onKeyDown={editing ? onEditorKeyDown : undefined}>
        {editing && editor ? (
          editor.render(close)
        ) : (
          <>
            {typeof value === 'string' ? <strong>{value}</strong> : value}
            {risk && <span className={classes.risk}>{risk}</span>}
            {help && <span className={classes.help}>{help}</span>}
            {changed && <ChangedMarker {...changed} />}
          </>
        )}
      </div>
      <div className={classes.actions}>
        {action}
        {editor && !editing && (
          <Button
            ref={editButton}
            variant="subtle"
            size="xs"
            onClick={() => setEditing(true)}
            aria-label={editAriaLabel}
          >
            {editLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
